import { prepareResponse, throwResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { WIRE_STATUS_DB } from '@/const/wire';
import { getRecipientWireApi } from '@/api/v1/recipient/getRecipientWireApi';
import { getRecipientPaymentDetailsApi } from '@/api/v1/recipient/getRecipientPaymentDetailsApi';
import { recipientChangeWireStatusApi } from '@/api/v1/recipient/recipientChangeWireStatusApi';
import {
  UpdateDomesticPaymentDetailsPayloadType,
  UpdateDomesticPaymentResponseType,
  updateWirePaymentDetailsDomesticApi,
} from '@/api/v1/organization/wires/updateWirePaymentDetailsDomesticApi';
import {
  UpdateInternationalPaymentDetailsPayloadType,
  UpdateInternationalPaymentResponseType,
  updateWirePaymentDetailsInternationalApi,
} from '@/api/v1/organization/wires/updateWirePaymentDetailsInternationalApi';
import { initRecipientDocumentVerificationApi } from '@/api/v1/recipient/initRecipientDocumentVerificationApi';
import { getRecipientTrustStampStatusApi } from '@/api/v1/recipient/getRecipientTrustStampStatusApi';
import { ConfirmPaymentDetailsDataType, confirmPaymentDetailsApi } from '@/api/v1/recipient/confirmPaymentDetailsApi';
import { ApiResponseType } from '@/types/sharedTypes';
import { getRecipientSourceWirePaymentDetailsApi } from '@/api/v1/recipient/getRecipientSourceWirePaymentDetailsApi';
import { IDEMPOTENCY_KEY, idempotencyKeyHandler } from '@/helpers/apiHelpers/requestHelpers';

type UpdatePaymentDetailsResponseType = ApiResponseType<
  UpdateDomesticPaymentResponseType | UpdateInternationalPaymentResponseType
>;

type UpdatePaymentDetailsPayloadType =
  | UpdateDomesticPaymentDetailsPayloadType
  | UpdateInternationalPaymentDetailsPayloadType;

export const loadRecipientWire = async (id: string) => {
  try {
    const response = await getRecipientWireApi(id);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const loadRecipientPaymentDetails = async (id: string) => {
  try {
    const response = await getRecipientPaymentDetailsApi(id);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const loadRecipientSourceWirePaymentDetails = async (wireId: string) => {
  try {
    const response = await getRecipientSourceWirePaymentDetailsApi(wireId);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const updatePaymentDetails = async (
  id: string,
  values: UpdatePaymentDetailsPayloadType,
  isDomesticPaymentType: boolean
) => {
  const { header, clearKey } = idempotencyKeyHandler(IDEMPOTENCY_KEY.PROVIDE_PAYMENT_DETAILS);

  try {
    const response: UpdatePaymentDetailsResponseType = isDomesticPaymentType
      ? await updateWirePaymentDetailsDomesticApi(id, values as UpdateDomesticPaymentDetailsPayloadType, header)
      : await updateWirePaymentDetailsInternationalApi(
          id,
          values as UpdateInternationalPaymentDetailsPayloadType,
          header
        );

    clearKey();

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const changeWireStatus = async (id: string, status: typeof WIRE_STATUS_DB.RECIPIENT_ASSIGNED) => {
  const body = {
    status,
  };

  try {
    const response = await recipientChangeWireStatusApi(id, body);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const initRecipientDocumentVerification = async (wireId: string) => {
  try {
    const response = await initRecipientDocumentVerificationApi(wireId);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const getRecipientTrustStampStatus = async (wireId: string) => {
  try {
    const response = await getRecipientTrustStampStatusApi(wireId);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const confirmPaymentDetails = async (wireId: string, payload?: ConfirmPaymentDetailsDataType) => {
  try {
    const response = await confirmPaymentDetailsApi(wireId, payload);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};
